import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import { adminApi } from "../api/adminApi";
import { getToken } from "../api/client";
import type { CurrentUser, Role } from "../api/types";

const USER_STORAGE_KEY = "reportforge.user";

interface AuthContextValue {
  user: CurrentUser | null;
  isAuthenticated: boolean;
  loading: boolean;
  login: (email: string, password: string, tenantSlug?: string) => Promise<void>;
  signup: (params: {
    companyName: string;
    slug: string;
    ownerFullName?: string;
    ownerEmail: string;
    password: string;
  }) => Promise<void>;
  /** Start a session from an auth response obtained elsewhere (the on-prem setup wizard). */
  startSession: (res: { userId: string; tenantId: string | null; roles: Role[] }, email: string) => void;
  logout: () => Promise<void>;
  hasRole: (...roles: Role[]) => boolean;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

function readStoredUser(): CurrentUser | null {
  const raw = localStorage.getItem(USER_STORAGE_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as CurrentUser;
  } catch {
    return null;
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<CurrentUser | null>(() =>
    getToken() ? readStoredUser() : null
  );
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (getToken() && !user) {
      setUser(readStoredUser());
    }
  }, [user]);

  // apiFetch fires this the moment ANY call comes back with a stale/invalid/expired token (see
  // client.ts's isSessionError) - clearing user state here is what makes ProtectedRoute notice
  // and redirect to /login, instead of the page that happened to make that call rendering a
  // blank screen because it had no other way to know the session was gone.
  useEffect(() => {
    function handleSessionExpired() {
      localStorage.removeItem(USER_STORAGE_KEY);
      setUser(null);
    }
    window.addEventListener("reportforge:session-expired", handleSessionExpired);
    return () => window.removeEventListener("reportforge:session-expired", handleSessionExpired);
  }, []);

  // LoginResponse/signup response is flat (token, userId, tenantId, roles) - no nested "user"
  // object and no profile fields (email/fullName) come back from the backend at all, so
  // CurrentUser is built from what the caller passed in plus the token's own claims.
  function applySession(res: { userId: string; tenantId: string | null; roles: Role[] }, email: string) {
    const currentUser: CurrentUser = {
      id: res.userId,
      tenantId: res.tenantId,
      email,
      roles: res.roles,
    };
    localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(currentUser));
    setUser(currentUser);
  }

  const login = useCallback(async (email: string, password: string, tenantSlug?: string) => {
    setLoading(true);
    try {
      const res = await adminApi.auth.login(email, password, tenantSlug);
      applySession(res, email);
    } finally {
      setLoading(false);
    }
  }, []);

  const signup = useCallback(
    async (params: {
      companyName: string;
      slug: string;
      ownerFullName?: string;
      ownerEmail: string;
      password: string;
    }) => {
      setLoading(true);
      try {
        const res = await adminApi.auth.signup(params);
        applySession(res, params.ownerEmail);
      } finally {
        setLoading(false);
      }
    },
    []
  );

  const logout = useCallback(async () => {
    await adminApi.auth.logout();
    localStorage.removeItem(USER_STORAGE_KEY);
    setUser(null);
  }, []);

  const hasRole = useCallback(
    (...roles: Role[]) => {
      if (!user) return false;
      return roles.some((r) => user.roles.includes(r));
    },
    [user]
  );

  const value = useMemo<AuthContextValue>(
    () => ({ user, isAuthenticated: !!user, loading, login, signup, startSession: applySession, logout, hasRole }),
    [user, loading, login, signup, logout, hasRole]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
